import { Dispatch, SetStateAction, useState } from "react";
import { AxiosError } from "axios";
import Link from "@mui/material/Link";
import { rejectRequestCancelTransaction } from "../../../services";
import { SaleStatus } from "../../../types/sale.type";
import { useUserRole } from "../../../utils/hooks/useUserRole";
import { pushGlobalError } from "../../../errors/globalError";
import { ErrorResponse } from "../../../errors/errorHandler";
import RejectCancelModal from "./RejectCancelModal";

const NOT_PENDING_CANCEL_MESSAGE = "Transaction is not in pending_cancel status";

interface RejectCancelLinkProps {
  uuid: string;
  status: SaleStatus;
  setTriggerFetch: Dispatch<SetStateAction<number>>;
}

export default function RejectCancelLink({
  uuid,
  status,
  setTriggerFetch,
}: RejectCancelLinkProps) {
  const { isBranchAccounting, isBranchFinance, isBranchFinanceAndAccount } =
    useUserRole();
  const canModerateCancel =
    isBranchAccounting || isBranchFinance || isBranchFinanceAndAccount;

  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  if (!canModerateCancel || status !== "pending_cancel") return null;

  const onConfirm = async (reason?: string) => {
    try {
      setLoading(true);
      await rejectRequestCancelTransaction(uuid, reason);
      setOpen(false);
      setTriggerFetch((prev) => prev + 1);
    } catch (error) {
      const axiosErr = error as AxiosError<ErrorResponse>;
      if (axiosErr?.response?.status !== 409) return;
      // The API answers 409 for two different reasons. Only a stale status means
      // the row moved under us; a live replacement bill (R9) is already toasted in
      // Thai by the interceptor, and overwriting it here would hide the real cause.
      if (axiosErr.response.data?.message === NOT_PENDING_CANCEL_MESSAGE) {
        pushGlobalError(
          "รายการนี้ไม่อยู่ในสถานะรอยกเลิกแล้ว ระบบจะรีเฟรชข้อมูลให้",
        );
        setTriggerFetch((prev) => prev + 1);
      }
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Link
        component="button"
        type="button"
        onClick={() => setOpen(true)}
        sx={{
          color: (theme) => theme.palette.link.main,
          fontWeight: 600,
          textDecoration: "underline",
          cursor: "pointer",
          background: "none",
          border: "none",
          padding: 0,
          font: "inherit",
        }}
      >
        ปฏิเสธ
      </Link>
      <RejectCancelModal
        open={open}
        loading={loading}
        onClose={() => (loading ? undefined : setOpen(false))}
        onConfirm={onConfirm}
      />
    </>
  );
}
